/**
 * RequestAccessPage — Iter 219 (Early Access)
 *
 * Public lead-capture form. Replaces the old self-serve register page
 * as the primary entry point for prospects. Does NOT create a workspace
 * or user. Sales team reaches out from the /admin/access-requests
 * inbox once the lead is qualified.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ShieldCheck, FileCheck, Users, Activity, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { accessRequestsApi } from '../lib/api';
import { toast } from 'sonner';

const PLAN_OPTIONS = [
  { value: 'undecided', label: 'Not sure yet', hint: "We'll help you pick after a scoping call." },
  { value: 'core', label: 'Core',
    hint: 'Single-lane RGR + duty repayment for small merchants. Best for teams filing under 500 returns / month.' },
  { value: 'growth', label: 'Growth',
    hint: 'Multi-lane workflows, historical audits, and broker collaboration. Best for scaling merchants under 10k returns / month.' },
  { value: 'enterprise', label: 'Enterprise',
    hint: 'Multi-workspace tenancy, SOC-2 evidence packs, priority HMRC support. Best for high-volume brokers and 3PLs.' },
];

const SIZE_OPTIONS = ['1-10', '11-50', '51-200', '200+'];
const VOLUME_OPTIONS = ['<500', '500-2,000', '2,000-10,000', '10,000+'];
const LANES = [
  { key: 'uk', label: 'UK' },
  { key: 'eu', label: 'EU' },
  { key: 'usa', label: 'USA' },
];

export default function RequestAccessPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    companyName: '',
    companySize: '',
    planOfInterest: 'undecided',
    monthlyReturnVolume: '',
    lanes: [],
    message: '',
    bookedDemo: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const toggleLane = (lane) => {
    setForm((prev) => ({
      ...prev,
      lanes: prev.lanes.includes(lane)
        ? prev.lanes.filter((l) => l !== lane)
        : [...prev.lanes, lane],
    }));
  };

  const submit = async (e, options = {}) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.companyName) {
      toast.error('Please fill in your name, email, and company.');
      return;
    }
    setSubmitting(true);
    try {
      await accessRequestsApi.submit({
        name: form.name,
        email: form.email,
        company_name: form.companyName,
        company_size: form.companySize || null,
        plan_of_interest: form.planOfInterest || null,
        monthly_return_volume: form.monthlyReturnVolume || null,
        lanes_of_interest: form.lanes,
        message: form.message || null,
        source: 'request_access_page',
        booked_demo: !!options.bookedDemo || form.bookedDemo,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Could not submit your request. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-primary" data-testid="request-access-page">
      {/* Left panel — brand + early-access positioning */}
      <aside className="hidden lg:flex lg:w-[42%] flex-col justify-between p-12 bg-primary text-white relative overflow-hidden" data-testid="request-left-panel">
        <div className="absolute inset-0 opacity-40 pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 25% 15%, var(--color-info) 0%, transparent 45%), radial-gradient(circle at 80% 90%, var(--color-primary-dark) 0%, transparent 50%)' }} />
        <div className="relative">
          <Link to="/login" className="flex items-center gap-2.5 mb-16" data-testid="request-brand-link">
            <img src="https://customer-assets.emergentagent.com/job_4cf3c2ed-55c0-4e2e-88f8-3504a875a61f/artifacts/0bt8uv0u_faviconM.png" alt="Meridian" className="h-9 w-9" />
            <span className="font-heading font-semibold text-xl tracking-tight">Meridian</span>
          </Link>
          <div className="inline-block px-2.5 py-1 rounded-full bg-white/15 border border-white/30 text-white text-xs font-medium tracking-wide mb-5">
            Early Access
          </div>
          <h2 className="text-4xl font-heading font-bold leading-[1.15] tracking-tight mb-4">
            Join the <span className="text-warning">Early Access</span><br />
            programme.
          </h2>
          <p className="text-white/90 text-[15px] leading-relaxed mb-10 max-w-md">
            Meridian Compliance is in Early Access. We provision workspaces individually so we can enable the right lanes and integrations for your operation.
          </p>
          <ul className="space-y-6">
            {[
              { icon: FileCheck, text: 'Item-level evidence packs for HMRC/CDS, with an audit-ready trail.' },
              { icon: ShieldCheck, text: 'Tenant-isolated workspace. Pilot-ready with controlled access.' },
              { icon: Users, text: 'Invite brokers and teammates with scoped, role-based access.' },
              { icon: Activity, text: 'Multi-layer matching, movement events, and filing readiness gates.' },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <li key={i} className="flex items-start gap-4 text-[14.5px] text-white" data-testid={`request-perk-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0 overflow-visible">
                    <Icon className="w-4 h-4 text-primary-dark flex-shrink-0" strokeWidth={2} />
                  </div>
                  <span className="pt-1.5 leading-[1.55] max-w-[380px]">{p.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="relative flex items-center gap-2 text-xs text-white/80">
          <ShieldCheck className="w-3 h-3" /> Pricing available on request. UK-hosted. Tenant-isolated.
        </div>
      </aside>

      {/* Right panel */}
      <main className="relative flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-6 overflow-hidden"
            style={{ background: 'linear-gradient(160deg, color-mix(in srgb, var(--color-primary) 14%, white) 0%, color-mix(in srgb, var(--color-primary) 6%, white) 45%, color-mix(in srgb, var(--color-nav-bg) 10%, white) 100%)' }}
            data-testid="request-right-panel">
        <div className="absolute inset-0 opacity-[0.12] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 85% 10%, var(--color-primary) 0%, transparent 35%), radial-gradient(circle at 10% 95%, var(--color-nav-bg) 0%, transparent 40%)' }} />
        <div className="relative max-w-lg w-full mx-auto">
          <div className="flex items-center gap-2.5 mb-4" data-testid="request-right-logo">
            <img src="https://customer-assets.emergentagent.com/job_4cf3c2ed-55c0-4e2e-88f8-3504a875a61f/artifacts/0bt8uv0u_faviconM.png" alt="Meridian" className="h-9 w-9" />
            <span className="font-heading font-semibold text-xl tracking-tight text-nav">Meridian</span>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4"
            data-testid="request-back-to-login"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>

          {submitted ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8" data-testid="request-thankyou">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 mb-3" />
              <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Thanks: your request is in.
              </h1>
              <p className="text-slate-600 text-[15px] leading-relaxed">
                A Meridian specialist will review your request and reach out to <strong>{form.email}</strong> within one business day to discuss your operation and next steps. A confirmation email is on its way.
              </p>
              <p className="text-slate-500 text-sm mt-4">
                Already have a workspace?{' '}
                <Link to="/login" className="font-medium text-primary hover:underline">Sign in</Link>.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-white/95 backdrop-blur-sm shadow-lg p-6" data-testid="request-form-card">
              <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight" data-testid="request-title">
                Request access
              </h1>
              <p className="text-[15px] text-slate-500 mt-1.5 mb-5">
                A Meridian specialist will review your request and follow up within one business day to discuss your operation and next steps.
              </p>

              <form onSubmit={submit} className="space-y-3" data-testid="request-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700">Your name</Label>
                    <Input id="name" value={form.name} onChange={(e) => update('name', e.target.value)}
                      className="h-11 rounded-lg border-slate-200" data-testid="request-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">Work email</Label>
                    <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                      className="h-11 rounded-lg border-slate-200" data-testid="request-email" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="company" className="text-sm font-medium text-slate-700">Company name</Label>
                  <Input id="company" value={form.companyName} onChange={(e) => update('companyName', e.target.value)}
                    className="h-11 rounded-lg border-slate-200" data-testid="request-company" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Company size</Label>
                    <Select value={form.companySize} onValueChange={(v) => update('companySize', v)}>
                      <SelectTrigger data-testid="request-company-size" className="h-11 rounded-lg border-slate-200">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {SIZE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s} employees</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">Monthly returns volume</Label>
                    <Select value={form.monthlyReturnVolume} onValueChange={(v) => update('monthlyReturnVolume', v)}>
                      <SelectTrigger data-testid="request-volume" className="h-11 rounded-lg border-slate-200">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {VOLUME_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s} / month</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Plan of interest</Label>
                  <Select value={form.planOfInterest} onValueChange={(v) => update('planOfInterest', v)}>
                    <SelectTrigger data-testid="request-plan" className="h-11 rounded-lg border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAN_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const selected = PLAN_OPTIONS.find((o) => o.value === form.planOfInterest);
                    return selected?.hint ? (
                      <p className="text-xs text-slate-500 leading-snug" data-testid="request-plan-hint">
                        {selected.hint}
                      </p>
                    ) : null;
                  })()}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Lanes of interest</Label>
                  <div className="flex flex-wrap gap-2">
                    {LANES.map((l) => (
                      <button
                        type="button"
                        key={l.key}
                        onClick={() => toggleLane(l.key)}
                        aria-pressed={form.lanes.includes(l.key)}
                        data-testid={`request-lane-${l.key}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          form.lanes.includes(l.key)
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-sm font-medium text-slate-700">Anything else? (optional)</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    rows={2}
                    className="rounded-lg border-slate-200"
                    placeholder="Which lanes / integrations matter most? Any deadlines?"
                    data-testid="request-message"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    type="submit"
                    className="flex-1 h-11 rounded-lg text-[15px] font-semibold"
                    style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                    disabled={submitting}
                    data-testid="request-submit"
                  >
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Request access
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-11 rounded-lg text-[15px] font-semibold"
                    disabled={submitting}
                    onClick={(e) => submit(e, { bookedDemo: true })}
                    data-testid="request-book-demo"
                  >
                    Book a demo
                  </Button>
                </div>

                <p className="text-xs text-slate-400 leading-relaxed pt-1">
                  Meridian Compliance is in early access. Submitting this form does not create a workspace, a specialist will contact you first.
                </p>
              </form>

              <div className="mt-5 pt-4 border-t border-slate-100 text-sm text-slate-500 text-center">
                Already have a workspace?{' '}
                <Link to="/login" className="font-medium text-primary hover:underline" data-testid="request-login-link">
                  Sign in
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
